/**
 * @file Defines pages authorization requirements
 */
import settingMenu from '@/modules/settings/settings-menu';
import automationsMenu from '@/modules/automations/automations-menu';
import auth from '../auth';
import DefaultValidator from '../validators/default-validator';

function settings() {
    const allowedMenu = settingMenu.find(
        ({route}) => route && route.name && auth.$pages[route.name].isAllowed,
    );
    return new DefaultValidator('Settings', {isAllowed: !!allowedMenu});
}
function automations() {
    const allowedMenu = automationsMenu.find(
        ({route}) => route && route.name && auth.$pages[route.name].isAllowed,
    );
    return new DefaultValidator('Settings', {isAllowed: !!allowedMenu});
}

const pages = {
    // guests
    login: () => auth.guest,
    logout: () => auth.guest,
    'forgot-password': () => auth.guest,
    signup: () => auth.guest,
    activate: () => auth.guest,
    'reset-password': () => auth.guest,
    'reset-password-redirect': () => auth.guest,

    // dashboard
    dashboard: () => auth.default,

    // settings
    settings,

    lists: () => auth.default,

    // automations
    automations,

    // search
    'search-results': () => auth.default,

    // guides
    guides: () => auth.default,
    'guide-notification-template': () => auth.default,

    // api-keys
    'api-keys': () => auth.apiKeys.read,
    'add-api-key': () => auth.apiKeys.add,
    'update-api-key': () => auth.apiKeys.edit,

    // blacklists
    blacklists: () => auth.blacklists.read,
    'add-blacklist': () => auth.blacklists.add,

    // checkout-pages
    'checkout-pages': () => auth.checkoutPages.read,
    'add-checkout-page': () => auth.checkoutPages.add,
    'update-checkout-page': () => auth.checkoutPages.edit,

    // coupons
    coupons: () => auth.coupons.read,
    'add-coupon': () => auth.coupons.add,
    'update-coupon': () => auth.coupons.edit,
    'coupon-details': () => auth.coupons.read,
    'coupons-redemptions': () => auth.coupons.read,

    // custom-fields
    'custom-fields': () => auth.customFields.read,
    'add-custom-field': () => auth.customFields.add,
    'update-custom-field': () => auth.customFields.edit,

    // credit memos
    'credit-memos': () => auth.creditMemos.read,
    'credit-memo-details': () => auth.creditMemos.read,

    // customers
    customers: () => auth.customers.read,
    'add-customer': () => auth.customers.add,
    'customer-details': () => auth.customers.read,

    // data-exports
    'data-exports': () => auth.dataExports.read,
    'add-data-export': () => auth.dataExports.add,
    'update-data-export': () => auth.dataExports.edit,

    // disputes
    'add-dispute': () => auth.disputes.read.add,
    'update-dispute': () => auth.disputes.read.add.edit,

    // gateway-accounts
    'gateway-accounts': () => auth.gatewayAccounts.read,
    'add-gateway-account': () => auth.gatewayAccounts.add,
    'update-gateway-account': () => auth.gatewayAccounts.edit,
    'gateway-account-details': () => auth.gatewayAccounts.read,

    // logs
    'resource-log': () => auth.webhooksTracking.read.or(auth.apiTracking.read),
    'webhook-tracking-logs': () => auth.webhooksTracking.read,
    'api-logs': () => auth.apiTracking.read,

    // notifications
    notifications: () => auth.emailNotifications,
    'view-notifications': () => auth.emailNotifications.read
        .and(auth.systemEvents.read).and(auth.rulesets.read),
    'add-notification': () => auth.emailNotifications.add
        .and(auth.systemEvents.read).and(auth.rulesets.read.add.edit),
    'edit-notification': () => auth.emailNotifications.edit
        .and(auth.systemEvents.read).and(auth.rulesets.read.add.edit),

    // orders
    orders: () => auth.subscriptions.read,
    'add-order': () => auth.subscriptions.add,
    'order-details': () => auth.subscriptions.read,
    'order-upcoming-invoice': () => auth.subscriptions.read && auth.invoices.read,

    // organizations
    organizations: () => auth.organizations.read,
    'add-organization': () => auth.organizations.add,
    'update-organization': () => auth.organizations.edit,
    'organization-details': () => auth.organizations.read,

    // plan
    'add-plan': () => auth.plans.add,

    // products
    products: () => auth.products.read,
    'add-product': () => auth.products.add,
    'product-details': () => auth.products.read,
    'update-product': () => auth.products.edit,

    // profile is aliased
    profile: () => auth.profile,

    // integrations
    integrations: () => auth.credentials.read,
    'integrations-google-sheets': () => auth.credentials.read,
    'integrations-google-sheets-add': () => auth.credentials.add,
    'integrations-google-sheets-edit': () => auth.credentials.edit,

    // invoices
    invoices: () => auth.invoices.read,
    'invoice-details': () => auth.invoices.read,

    // rules-engine
    'rules-engine': () => auth.rulesEngine,
    'system-events': () => auth.systemEvents.read,
    'value-lists': () => auth.lists.read,
    'add-list-item': () => auth.lists.read.add,
    'edit-list-item': () => auth.lists.read.edit,
    'history-list-item': () => auth.lists.read.edit,
    'view-system-event': () => auth.systemEvents.and(auth.rulesets.read),
    'view-system-event-history': () => auth.systemEvents.read.edit,
    'add-system-rule': () => auth.systemEvents.add.and(auth.rulesets.read.add),
    'add-system-bind': () => auth.systemEvents.add.and(auth.rulesets.read.add),
    'edit-system-rule': () => auth.systemEvents.edit.and(auth.rulesets.read.add.edit),
    'edit-system-bind': () => auth.systemEvents.edit.and(auth.rulesets.read.add.edit),

    // segments
    segments: () => auth.default,
    'segment-management': () => auth.default,
    'add-segment': () => auth.gridSegments.read.add.edit,
    // TODO 'resource-log'

    // shipping zones
    'shipping-zones': () => auth.shippingZones.read,
    'add-shipping-zone': () => auth.shippingZones.read.add.edit,
    'edit-shipping-zone': () => auth.shippingZones.read.edit,

    // transactions
    transactions: () => auth.transactions.read,
    'transaction-details': () => auth.transactions.read,

    // tags
    tags: () => auth.tags.read,

    // users
    users: () => auth.users.read,
    'add-user': () => auth.users.read.add.edit,
    'update-user': () => auth.users.read.edit,

    // webhooks
    webhooks: () => auth.webhooks.read,
    'add-webhook': () => auth.webhooks.add,
    'update-webhook': () => auth.webhooks.edit,

    // websites
    'add-website': () => auth.websites.add,
    'update-website': () => auth.websites.edit,
};

export default pages;
